import { isLeft, isRight } from 'fp-ts/Either';
import { use, useState } from 'react';
import { toast } from 'sonner';
import { TRPCContext } from '../../contexts/TRPC';
import {
  groupRejectionReasonsByCategory,
  useRejectionReasons,
  type RejectionReasonOption,
} from '../../hooks/useRejectionReasons';
import { captureAndReportError } from '../../utils/errorReporting';
import { Button } from '../ui/button';
import {
  emptyRejectionReasonForm,
  RejectionReasonForm,
  REJECTION_REASON_CATEGORIES,
  type RejectionReasonFormState,
} from './RejectionReasonForm';

const sortReasons = (reasons: RejectionReasonOption[]) =>
  [...reasons].sort(
    (a, b) =>
      a.category.localeCompare(b.category) || a.code.localeCompare(b.code),
  );

export function RejectionReasonManagementSection() {
  const trpc = use(TRPCContext);
  const {
    rejectionReasonOptions: reasons,
    setRejectionReasonOptions: setReasons,
    isRejectionReasonsLoading: isLoading,
    rejectionReasonsError: error,
  } = useRejectionReasons(trpc);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [form, setForm] = useState<RejectionReasonFormState>(
    emptyRejectionReasonForm,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const upsertReason = (reason: RejectionReasonOption) => {
    setReasons((current) => {
      const index = current.findIndex((entry) => entry.id === reason.id);
      if (index === -1) {
        return sortReasons([...current, reason]);
      }
      const next = [...current];
      next[index] = reason;
      return sortReasons(next);
    });
  };

  const openCreateForm = () => {
    setEditingId(null);
    setForm(emptyRejectionReasonForm);
    setIsFormOpen(true);
  };

  const openEditForm = (reason: RejectionReasonOption) => {
    setEditingId(reason.id);
    setForm({
      code: reason.code,
      label: reason.label,
      description: reason.description,
      category: reason.category,
    });
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingId(null);
    setForm(emptyRejectionReasonForm);
  };

  const updateField = (
    field: keyof RejectionReasonFormState,
    value: string,
  ) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      const result = editingId
        ? await trpc.client.updateRejectionReason.mutate({
            id: editingId,
            label: form.label,
            description: form.description,
            category:
              form.category as (typeof REJECTION_REASON_CATEGORIES)[number],
          })
        : await trpc.client.createRejectionReason.mutate({
            code: form.code.trim(),
            label: form.label,
            description: form.description,
            category:
              form.category as (typeof REJECTION_REASON_CATEGORIES)[number],
          });

      if (isRight(result)) {
        upsertReason(result.right);
        toast.success(
          editingId ? 'Rejection reason updated' : 'Rejection reason created',
        );
        closeForm();
        return;
      }
      if (isLeft(result)) {
        toast.error(result.left.message);
      }
    } catch (submitError) {
      captureAndReportError('Error saving rejection reason:', submitError);
      toast.error('Failed to save rejection reason');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (reason: RejectionReasonOption) => {
    setTogglingId(reason.id);
    try {
      const result = await trpc.client.updateRejectionReason.mutate({
        id: reason.id,
        isActive: !reason.isActive,
      });
      if (isRight(result)) {
        upsertReason(result.right);
        toast.success(
          result.right.isActive
            ? `${reason.code} activated`
            : `${reason.code} deactivated`,
        );
        return;
      }
      if (isLeft(result)) {
        toast.error(result.left.message);
      }
    } catch (toggleError) {
      captureAndReportError('Error toggling rejection reason:', toggleError);
      toast.error('Failed to update rejection reason');
    } finally {
      setTogglingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="size-5 animate-spin rounded-full border-2 border-neutral-700 border-t-neutral-300" />
      </div>
    );
  }

  if (error) {
    return <div className="text-sm text-red-400">{error}</div>;
  }

  const groups = groupRejectionReasonsByCategory(reasons);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-neutral-500">
          {reasons.filter((reason) => reason.isActive).length} active /{' '}
          {reasons.length} total
        </p>
        <Button
          type="button"
          size="sm"
          className="bg-teal-600 text-white hover:bg-teal-500"
          onClick={openCreateForm}
        >
          Add reason
        </Button>
      </div>

      {isFormOpen && (
        <RejectionReasonForm
          form={form}
          isEditing={editingId !== null}
          isSaving={isSaving}
          onFieldChange={updateField}
          onSubmit={() => {
            void handleSubmit();
          }}
          onCancel={closeForm}
        />
      )}

      <div className="space-y-4">
        {groups.map((group) => (
          <div key={group.category} className="space-y-1.5">
            <div className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
              {group.category}
            </div>
            <ul className="divide-y divide-neutral-800/70 rounded-lg border border-neutral-800/80 bg-neutral-900/40">
              {group.items.map((reason) => (
                <li
                  key={reason.id}
                  className="flex flex-col gap-2 px-3 py-2 md:flex-row md:items-center md:gap-3"
                >
                  <div className="flex min-w-0 flex-col gap-0.5 md:flex-1 md:flex-row md:items-center md:gap-3">
                    <span className="w-20 shrink-0 font-mono text-xs text-neutral-400">
                      {reason.code}
                    </span>
                    <span
                      className="min-w-0 flex-1 text-sm text-neutral-200 md:truncate"
                      title={reason.description}
                    >
                      {reason.label}
                    </span>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-2 md:gap-3">
                    {!reason.isActive && (
                      <span className="shrink-0 rounded-full bg-neutral-800/80 px-2 py-0.5 text-[11px] font-medium text-neutral-400 ring-1 ring-inset ring-neutral-700/60">
                        Inactive
                      </span>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs text-neutral-400 hover:text-white"
                      disabled={isSaving || togglingId !== null}
                      onClick={() => openEditForm(reason)}
                    >
                      Edit
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 w-24 shrink-0 px-2 text-xs text-neutral-400 hover:text-white"
                      disabled={togglingId !== null}
                      onClick={() => {
                        void handleToggleActive(reason);
                      }}
                    >
                      {togglingId === reason.id
                        ? '…'
                        : reason.isActive
                          ? 'Deactivate'
                          : 'Activate'}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
